import { useState } from "react";
import { acceptAmount } from "../lib/money";
import { CodeInput, PasswordInput } from "./AuthShell";
import { Icon } from "./icons";
import { Alert, Button, Field, Input } from "./ui";
import {
  adjustmentFieldErrors,
  centsAsMoney,
  type AdjustmentDraft,
  type AdjustmentFieldErrors,
} from "../lib/adjustments";
import type { AdjustCreditInput } from "../lib/queries";
import type { AdjustmentPolicy } from "../lib/types";

/**
 * The adjustment dialog's body: the amount and reason, the step-up that
 * proves the session is still the operator's, and — first, above both — what
 * applying it will do.
 *
 * The readout is the point. Every rule the route enforces is stated here in
 * the operator's own figures before they type a password, because a limit
 * learned from a 403 after the fact is a limit the console hid. The numbers
 * come from the API's `adjustmentPolicy` rather than from constants of this
 * repository's own, so the dialog cannot quote a figure the route no longer
 * holds.
 *
 * Validation is per field and appears on the first attempt to apply. Every
 * field is required and each one's hint says so before anything is typed;
 * after an attempt, the fields that still need attention say what is wrong
 * under the box itself, and the message clears as the operator fixes it. A
 * single sentence at the top — "a reason is required" — was the old shape,
 * and it left the operator to work out which of four boxes it meant.
 *
 * Mounted fresh each time the modal opens, so its state starts empty — the
 * password and code in particular must not survive from one adjustment to the
 * next.
 */
export function AdjustCreditForm({
  policy,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  policy: AdjustmentPolicy;
  pending: boolean;
  /** The API's own refusal, shown as sent — each says what to do next. */
  error: string | null;
  onSubmit: (input: AdjustCreditInput) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<AdjustmentDraft>({
    amount: "10.00",
    reason: "",
    password: "",
    code: "",
  });
  const [attempted, setAttempted] = useState(false);

  // Derived on every render rather than stored, so a field's message goes
  // away the keystroke it stops being true. Shown only after an attempt: a
  // dialog that opens with four red marks before anything has been typed is
  // shouting, not helping.
  const errors: AdjustmentFieldErrors = adjustmentFieldErrors(draft, policy);
  const shown: AdjustmentFieldErrors = attempted ? errors : {};
  const outstanding = Object.keys(errors).length;

  function update<K extends keyof AdjustmentDraft>(key: K, value: AdjustmentDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function submit() {
    setAttempted(true);
    if (outstanding > 0) return;

    onSubmit({
      amountCents: Math.round(Number(draft.amount) * 100),
      reason: draft.reason.trim(),
      password: draft.password,
      code: draft.code,
    });
  }

  return (
    <div className="space-y-4">
      {error && <Alert>{error}</Alert>}

      <AdjustmentPolicyNotice policy={policy} />

      <p className="text-xs text-fg-subtle">All four fields are required.</p>

      <Field
        label="Amount"
        htmlFor="adjust-amount"
        hint="Required. In the billing currency; negative removes credit."
        error={shown.amount}
      >
        {/*
          `inputMode` rather than `type="number"`. A scroll over a focused
          number field silently changes its value, and this one writes straight
          to a customer's balance — the guards around this form are about who
          may adjust and by how much, not about the operator's mouse.
        */}
        <Input
          id="adjust-amount"
          inputMode="decimal"
          required
          aria-invalid={shown.amount ? true : undefined}
          value={draft.amount}
          onChange={(event) => {
            // Negative is the point here: it is how credit is taken back.
            const accepted = acceptAmount(event.target.value, { negative: true });
            if (accepted !== null) update("amount", accepted);
          }}
        />
      </Field>

      <Field
        label="Reason"
        htmlFor="adjust-reason"
        hint="Required. Shown to the customer on their ledger, and to every operator in the alert."
        error={shown.reason}
      >
        <Input
          id="adjust-reason"
          required
          aria-invalid={shown.reason ? true : undefined}
          value={draft.reason}
          placeholder="Goodwill credit for the 12 March outage"
          onChange={(event) => update("reason", event.target.value)}
        />
      </Field>

      {/*
        The step-up, set apart from the amount it authorises. The session was
        proven at login, possibly hours ago on a machine since left open, so
        moving money asks again — the way turning two-factor off does.
      */}
      <div className="space-y-3 border-t border-line pt-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-fg">Confirm it's you</p>
          <p className="text-xs text-fg-subtle">
            Your password and a current code from your authenticator. Moving money asks again even
            though you're signed in, because this tab may not be yours any more.
          </p>
        </div>

        <Field
          label="Password"
          htmlFor="adjust-password"
          hint="Required. Your own password, not the customer's."
          error={shown.password}
        >
          <PasswordInput
            id="adjust-password"
            size="md"
            surface="panel"
            autoComplete="current-password"
            value={draft.password}
            onChange={(event) => update("password", event.target.value)}
          />
        </Field>

        {/*
          Not wrapped in a Field: the code input carries its own visually hidden
          label, and a second visible one for the same control would be read
          twice. The heading is hidden from assistive technology for that
          reason, and the hint and error are rendered by hand in Field's shape.
        */}
        <div className="space-y-1.5">
          <p aria-hidden="true" className="text-sm font-medium text-fg">
            Authenticator code
          </p>
          <CodeInput
            id="adjust-code"
            label="Authenticator code"
            value={draft.code}
            onChange={(value) => update("code", value)}
          />
          {shown.code ? (
            <p className="flex items-center gap-1.5 text-sm text-danger">
              <Icon name="warning" className="size-3.5 shrink-0" />
              {shown.code}
            </p>
          ) : (
            <p className="text-sm text-fg-subtle">Required. The six digits your app shows right now.</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {/*
          A count beside the button, so an operator whose eye is on the button
          knows to look up without hunting — the marks themselves are under
          the fields. Only after an attempt, like the marks.
        */}
        {attempted && outstanding > 0 && (
          <p role="status" className="text-sm text-danger">
            {outstanding === 1 ? "1 field needs attention." : `${outstanding} fields need attention.`}
          </p>
        )}
        <Button variant="secondary" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
        <Button loading={pending} onClick={submit}>
          Apply adjustment
        </Button>
      </div>
    </div>
  );
}

/**
 * What the route will do with this adjustment, in the operator's own figures.
 *
 * Four sentences, each one of the guards. The first is the only one that
 * changes per operator — the window is theirs, and two people looking at the
 * same organization read different remainders.
 */
export function AdjustmentPolicyNotice({ policy }: { policy: AdjustmentPolicy }) {
  return (
    <div className="space-y-1.5 rounded-md border border-line bg-canvas px-3.5 py-3 text-xs text-fg-subtle">
      <p className="font-medium text-fg">What happens when you apply this</p>
      <ul className="list-disc space-y-0.5 pl-4">
        <li>
          Up to {centsAsMoney(policy.perAdjustmentCents)} per adjustment. You can still move{" "}
          {centsAsMoney(policy.remainingForYouCents)} of your {centsAsMoney(policy.perOperatorWindowCents)} in
          any {policy.windowHours}-hour period ({centsAsMoney(policy.usedByYouCents)} used).
        </li>
        <li>Recorded in the audit log against your account — applied or refused.</li>
        <li>Every platform operator is emailed the amount, the reason and your name, immediately.</li>
        <li>A credit on an account that has never paid opens AI inference to it.</li>
      </ul>
    </div>
  );
}
